import {
  createDateOnly,
  type DateOnly,
  DateOnlyClass,
  FIXING_INDEX_MARK,
  type FixingIndex,
  type FixingIndexMarkMessageData,
  formattedDateForSubscription,
  type SubscriptionResponse,
  useObservableValue,
  useSubscription,
  wsScanToMap,
} from '@talos/kyoko';
import { formatInTimeZone } from 'date-fns-tz';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { map, shareReplay } from 'rxjs/operators';
import { useFixingIndices } from '../providers/FixingIndexProvider';

/** Given an IndexMark and its associated Index's time zone, will return a DateOnly variable in this time zone */
function getMarkDateOnlyInNominalTimezone(
  mark: FixingIndexMarkMessageData,
  indexTimeZone: string | undefined
): DateOnly | undefined {
  if (mark.NominalTime == null || indexTimeZone == null) {
    return undefined;
  }

  try {
    return createDateOnly(formatInTimeZone(mark.NominalTime, indexTimeZone, 'yyyy-MM-dd')) ?? undefined;
  } catch {
    return undefined;
  }
}

/** Given an array of marks (any date/time), and a targetDate DateOnly, will return all the marks which fall on this targetDate (in their respective tz) */
export function getIndexMarksForDate({
  marks,
  fixingIndicesByName,
  targetDate,
}: {
  marks: FixingIndexMarkMessageData[];
  fixingIndicesByName: Map<string, FixingIndex>;
  targetDate: DateOnly;
}): FixingIndexMarkMessageData[] {
  return compact(
    marks.map(mark => {
      if (mark.Index == null) {
        return undefined;
      }

      const fixingIndex = fixingIndicesByName.get(mark.Index);
      if (!fixingIndex) {
        return undefined;
      }

      const markDateOnly = getMarkDateOnlyInNominalTimezone(mark, fixingIndex.NominalIndexTimeZone);
      if (markDateOnly == null) {
        return undefined;
      }

      return new DateOnlyClass(targetDate).equals(markDateOnly) ? mark : undefined;
    })
  );
}

/**
 * Returns index marks keyed by fixing index name for a target date.
 * Subscribes to a +-2 day range around the target date to avoid missing relevant updates.
 *
 * Will perform comparison per mark in the timezone of the mark's index
 */
export function useIndexMarksOnDate(targetDate: DateOnly | undefined): Map<string, FixingIndexMarkMessageData> {
  const { fixingIndicesByName } = useFixingIndices();

  const fixingIndexMarksRequest = useMemo(() => {
    if (targetDate == null) {
      return null;
    }

    // We go +2/-2 days in the request because we ask for _all marks_, which might reflect indexes that are in _different time zones_.
    // This means that we cannot just say "subscribe for this 24h period in this timezone", becuase in reality, the total span is probably more than that.
    // So we go with a simpler approach - just do +2/-2 ensuring that we get all relevant marks (and more), and then we compare based on tz locally.
    const dateOnly = new DateOnlyClass(targetDate);
    const startDate = dateOnly.subDays(2);
    const endDate = dateOnly.addDays(2);
    if (startDate == null || endDate == null) {
      return null;
    }

    return {
      name: FIXING_INDEX_MARK,
      tag: 'useIndexMarksOnDate',
      StartDate: formattedDateForSubscription(startDate),
      EndDate: formattedDateForSubscription(endDate),
    };
  }, [targetDate]);

  const fixingIndexMarksSubscription =
    useSubscription<SubscriptionResponse<FixingIndexMarkMessageData>>(fixingIndexMarksRequest);

  const marks = useObservableValue(
    () =>
      fixingIndexMarksSubscription.data.pipe(
        wsScanToMap({ getUniqueKey: mark => mark.ID, newMapEachUpdate: false }),
        map(mapping => [...mapping.values()]),
        shareReplay({ bufferSize: 1, refCount: true })
      ),
    [fixingIndexMarksSubscription.data]
  );

  const output = useMemo(() => {
    if (targetDate == null || marks == null) {
      return new Map();
    }

    const targetDateMarks = getIndexMarksForDate({ marks, targetDate, fixingIndicesByName });
    const marksByIndexName = new Map(
      compact(
        targetDateMarks.map(mark => {
          return mark.Index ? [mark.Index, mark] : undefined;
        })
      )
    );

    return marksByIndexName;
  }, [targetDate, marks, fixingIndicesByName]);

  return output;
}
